import imageCompression from "browser-image-compression";
import { useEffect, useState } from "react";
import { ImagePrefixEnum, useGetImageDetailsMutation, useGetPresignedImageUrlMutation } from "../graphql/graphql";
export const useImageUpload = () => {
    const [compressedImage, setCompressedImage] = useState(null);
    const [imageData, setImageData] = useState(null);
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const [{ fetching: fetchingPresignedUrl }, getPresignedUrl] = useGetPresignedImageUrlMutation();
    const [{ fetching: fetchingImageDetails }, getImageDetails] = useGetImageDetailsMutation();
    // This requests a presigned URL and starts uploading the image as soon as it's done being compressed.
    // Once the image is uploaded, it fetches the image details and sets the image key and version in the form.
    useEffect(() => {
        if (compressedImage) {
            const safeImageName = `${Date.now()}-${compressedImage.name.replace(/[^a-zA-Z0-9]/g, "-")}`;
            getPresignedUrl({
                input: {
                    imagePrefix: ImagePrefixEnum.Avatars,
                    imageName: safeImageName,
                    contentType: compressedImage.type
                }
            }).then(({ data }) => {
                const { presignedUrl } = data?.images?.presignedUrl ?? {};
                if (!presignedUrl) {
                    throw new Error("No presigned URL found");
                }
                uploadImageWithPresignedUrl({
                    imageFile: compressedImage,
                    presignedUrl: presignedUrl
                }).then(async (result) => {
                    if (!result.ok) {
                        throw new Error("Image upload failed");
                    }
                    const { data } = await getImageDetails({
                        imagePrefix: ImagePrefixEnum.Avatars,
                        imageKey: safeImageName
                    });
                    const { imageKey, imageVersion } = data?.images?.imageDetails ?? {};
                    setImageData({
                        imageKey,
                        imageVersion
                    });
                });
            });
        }
    }, [compressedImage]);
    /**
     * Compresses an image to a maximum size of 3MB and a maximum width or height of 500px
     * @param file The image file to compress
     * @returns The compressed image file
     */
    const compressImage = async (file) => {
        let compressedFile = file;
        const options = {
            maxSizeMB: 3,
            maxWidthOrHeight: 500,
            useWebWorker: true
        };
        while (compressedFile.size > 3 * 1024 * 1024) {
            try {
                // Reduce quality or dimensions for each iteration
                options.maxWidthOrHeight -= 100;
                compressedFile = await imageCompression(compressedFile, options);
            }
            catch (error) {
                console.error(error);
                throw new Error("Error compressing the image.");
            }
        }
        return compressedFile;
    };
    /**
     * Uploads an image to S3 via a presigned URL
     * @param imageFile The image file to upload
     * @param presignedUrl The presigned URL to upload the image to
     */
    const uploadImageWithPresignedUrl = async ({ imageFile, presignedUrl }) => {
        setIsUploadingImage(true);
        try {
            const response = await fetch(presignedUrl, {
                method: "PUT",
                body: imageFile,
                headers: {
                    "Content-Type": imageFile.type
                }
            });
            if (!response.ok) {
                throw new Error(`Image upload failed: ${response.statusText}`);
            }
            return response;
        }
        catch (error) {
            console.error(error);
            throw error; // rethrow if needed
        }
        finally {
            setIsUploadingImage(false);
        }
    };
    return {
        imageData,
        setCompressedImage,
        fetchingPresignedUrl,
        compressImage,
        isUploadingImage,
        fetchingImageDetails
    };
};
